/**
 * ActivityTimelinePage
 *
 * Displays the customer's activity history in a vertical timeline format.
 * Supports filtering by activity type and pagination.
 * Read-only — customers can view but not create activities.
 *
 * Requirements: Task 8 - Activity Timeline Page
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { Activity, RefreshCw, ChevronLeft, ChevronRight } from '@bloomcode/icons';
import { type ActivityType } from '@bloomcode/graphql';

import { useMyActivities } from '@/hooks/useMyActivities';
import { TimelineItem, ActivityTypeFilter } from '@/components/activities';

/**
 * Empty state when no activities exist
 */
function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <Activity className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          {hasFilter ? 'No Matching Activities' : 'No Activities Yet'}
        </h3>
        <p className="text-muted-foreground max-w-sm">
          {hasFilter
            ? 'No activities match the selected filter. Try selecting a different type.'
            : 'When your account team logs interactions with you, they will appear here.'}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Pagination controls
 */
function PaginationControls({
  page,
  totalPages,
  hasNext,
  hasPrev,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Loading skeleton for the timeline
 */
function TimelineSkeleton() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="h-9 w-9 animate-pulse rounded-full bg-muted shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ActivityTimelinePage() {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<ActivityType[] | undefined>(undefined);

  const { activities, pagination, totalCount, isLoading, error, refetch } = useMyActivities({
    page,
    limit: 15,
    typeFilter,
  });

  const handleFilterChange = (filter: ActivityType[] | undefined) => {
    setTypeFilter(filter);
    setPage(1); // Reset to page 1 when filter changes
  };

  // Loading state
  if (isLoading && activities.length === 0) {
    return (
      <>
        <Helmet>
          <title>Activity History | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">Activity History</h1>
            <p className="text-muted-foreground">
              View your interaction history with the business.
            </p>
          </div>
          <div className="mb-4">
            <ActivityTypeFilter activeFilter={typeFilter} onFilterChange={handleFilterChange} />
          </div>
          <TimelineSkeleton />
        </div>
      </>
    );
  }

  // Error state
  if (error && activities.length === 0) {
    return (
      <>
        <Helmet>
          <title>Activity History | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">Activity History</h1>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load activities</p>
              <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Activity History | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Activities</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Activity History</h1>
            <p className="text-muted-foreground">
              {totalCount > 0
                ? `${totalCount} activit${totalCount !== 1 ? 'ies' : 'y'} recorded`
                : 'View your interaction history with the business.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            aria-label="Refresh activities"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Type Filter */}
        <div className="mb-4">
          <ActivityTypeFilter activeFilter={typeFilter} onFilterChange={handleFilterChange} />
        </div>

        {/* Timeline or Empty State */}
        {activities.length === 0 ? (
          <EmptyState hasFilter={!!typeFilter && typeFilter.length > 0} />
        ) : (
          <>
            <Card>
              <CardContent className="p-6">
                <div role="list" aria-label="Activity timeline">
                  {activities.map((activity, index) => (
                    <TimelineItem
                      key={activity.id}
                      activity={activity}
                      isLast={index === activities.length - 1}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <PaginationControls
                page={pagination.page}
                totalPages={pagination.pages}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
